import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import {
  Box,
  Button,
  Card,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { describeNext, favouriteText, updatedShortText } from "../program-landing";
import { FavouriteButton, ProgramBadge, ProgramTitleLink, visuallyHidden } from "./program-parts";

import type { Favourite } from "../program-landing";
import type { FC } from "react";

/** The favourites shown before "View N more". */
const FIRST = 3;

const headSx = { typography: "overline", lineHeight: 1, color: "text.secondary" } as const;

const FavouriteCard: FC<{ favourite: Favourite; onToggle: () => void }> = ({
  favourite,
  onToggle,
}) => (
  <Card
    variant="outlined"
    sx={{
      position: "relative",
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      py: 1.5,
      pl: 1.75,
      pr: 1,
      borderRadius: 2,
      "&:hover .program-title": { color: "primary.dark" },
    }}
  >
    <ProgramBadge node={favourite.node} size={40} />
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle1" component="span" noWrap sx={{ fontWeight: 600 }}>
        <ProgramTitleLink node={favourite.node} />
      </Typography>
      <Typography variant="caption" noWrap sx={{ color: "text.secondary" }}>
        {favouriteText(favourite)}
      </Typography>
    </Stack>
    <FavouriteButton node={favourite.node} favourite onToggle={onToggle} />
  </Card>
);

const FavouriteTable: FC<{
  favourites: Favourite[];
  today: Date;
  onToggle: (favourite: Favourite) => void;
}> = ({ favourites, today, onToggle }) => (
  <Paper variant="outlined" sx={{ overflowX: "auto", borderRadius: 2 }}>
    <Table aria-label="Favourites" size="small" sx={{ minWidth: 760 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...headSx, width: 56 }}>
            <Box component="span" sx={visuallyHidden}>
              Favourite
            </Box>
          </TableCell>
          <TableCell sx={headSx}>Name</TableCell>
          <TableCell sx={{ ...headSx, width: 100 }}>Type</TableCell>
          <TableCell align="right" sx={{ ...headSx, width: 140 }}>
            Communications
          </TableCell>
          <TableCell sx={{ ...headSx, width: 240 }}>Next send</TableCell>
          <TableCell sx={{ ...headSx, width: 120 }}>Updated</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {favourites.map((favourite) => {
          const summary = favourite.kind === "program" ? favourite.summary : null;
          const next = summary ? describeNext(summary) : null;

          return (
            <TableRow
              key={favourite.node.id}
              hover
              sx={{ position: "relative", "&:hover .program-title": { color: "primary.dark" } }}
            >
              <TableCell sx={{ pl: 1.5, pr: 0 }}>
                <FavouriteButton
                  node={favourite.node}
                  favourite
                  onToggle={() => onToggle(favourite)}
                />
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0 }}>
                  <ProgramBadge node={favourite.node} size={32} />
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" component="span">
                      <ProgramTitleLink node={favourite.node} />
                    </Typography>
                    {favourite.kind === "folder" ? (
                      <Typography variant="caption" noWrap sx={{ color: "text.secondary" }}>
                        In {favourite.path.join(" › ")}
                      </Typography>
                    ) : null}
                  </Stack>
                </Stack>
              </TableCell>
              <TableCell>{favourite.kind === "program" ? "Program" : "Folder"}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {summary ? summary.communications : "—"}
              </TableCell>
              <TableCell>
                <Stack sx={{ maxWidth: 260 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {next ? next.short : "—"}
                  </Typography>
                  {next ? (
                    <Typography variant="caption" noWrap sx={{ color: "text.secondary" }}>
                      {next.detail}
                    </Typography>
                  ) : null}
                </Stack>
              </TableCell>
              <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                {(summary && updatedShortText(summary, today)) ?? "—"}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </Paper>
);

/**
 * The caller's favourite programs and folders, in the order they were starred: the first three,
 * and the others after "View N more". With none, a line says how to add one.
 */
export const FavouritesSection: FC<{
  favourites: Favourite[];
  layout: "grid" | "table";
  today: Date;
  onToggle: (favourite: Favourite) => void;
}> = ({ favourites, layout, today, onToggle }) => {
  const [expanded, setExpanded] = useState(false);

  if (!favourites.length) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", color: "text.secondary" }}>
        <StarBorderIcon aria-hidden fontSize="small" />
        <Typography variant="body2">
          Star a program or folder to keep it here for quick access.
        </Typography>
      </Stack>
    );
  }

  const shown = expanded ? favourites : favourites.slice(0, FIRST);
  const more = favourites.length - FIRST;

  return (
    <Stack component="section" aria-labelledby="favourites-heading" spacing={1.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <StarIcon aria-hidden sx={{ fontSize: 20, color: "warning.dark" }} />
        <Typography
          id="favourites-heading"
          variant="subtitle1"
          component="h2"
          sx={{ fontWeight: 700 }}
        >
          Favourites
        </Typography>
        <Box
          sx={{
            px: 1,
            borderRadius: 11,
            bgcolor: "action.hover",
            color: "text.secondary",
            typography: "caption",
            fontWeight: 600,
          }}
        >
          {favourites.length}
        </Box>
      </Stack>
      {layout === "table" ? (
        <FavouriteTable favourites={shown} today={today} onToggle={onToggle} />
      ) : (
        <Box
          component="ul"
          aria-label="Favourites"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(320px, 100%), 1fr))",
            gap: 2,
          }}
        >
          {shown.map((favourite) => (
            <Box
              component="li"
              key={favourite.node.id}
              sx={{ display: "flex", "& > *": { flex: 1 } }}
            >
              <FavouriteCard favourite={favourite} onToggle={() => onToggle(favourite)} />
            </Box>
          ))}
        </Box>
      )}
      {more > 0 ? (
        <Button
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ alignSelf: "flex-start" }}
          data-analytics-id="programs-landing-favourites-more"
        >
          {expanded ? "Show fewer" : `View ${more} more`}
        </Button>
      ) : null}
    </Stack>
  );
};
